import { graphHooks } from '@tunasong/graph-lib/react'
import { logger, shortUuid } from '@tunasong/models'
import { useGlobalAppContext, usePlugins } from '@tunasong/plugin-lib'
import { features, useThunkDispatch } from '@tunasong/redux'
import { useAlert, useNavigateToEntity } from '@tunasong/ui-lib'
import { useRef, useState } from 'react'

/**
 * The Home page's create actions: the header's New song split button and the first-run start tiles.
 *
 * New items land at the root, which Home lists as Unfiled; the user can move them into a space later.
 */
export const useHomeCreate = () => {
  const plugins = usePlugins('all')
  const globalContext = useGlobalAppContext()
  const navigateToEntity = useNavigateToEntity()
  const dispatch = useThunkDispatch()
  const graph = graphHooks.useEntityGraph()
  // Cached-graph create (also populates the graph cache)
  const { createEntity } = graphHooks.useEntityCreate()
  const [busy, setBusy] = useState(false)
  /**
   * The create in flight, read synchronously: a second tap lands before `busy` re-renders, and used to create a
   * second song (GH #2489)
   */
  const inFlight = useRef(false)
  const { alert } = useAlert()

  /** Run one create at a time; a failed one says so, the way the create dialog does, instead of failing silently */
  const guarded = async (type: string, create: () => Promise<void>) => {
    if (inFlight.current) {
      return
    }
    inFlight.current = true
    setBusy(true)
    try {
      await create()
    } catch (error) {
      logger.warn('Failed to create entity', { type, error })
      alert({ severity: 'error', message: `Could not create the ${type}. Please try again.` })
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }

  /**
   * Mirrors the CreateEntityDialog flow (apps/tunasong/tunasong-ui/src/entity/create-entity-dialog.tsx) so the
   * new entity is fully scaffolded: the plugin template provides the seed content, the id is set up front,
   * template.onCreate runs (a band space creates its calendar there), and navigation prefers the plugin's
   * `Edit` layout so the user lands in the editor.
   */
  const createFromTemplate = async (type: string, name: string) => {
    const plugin = plugins?.find(p => p.node?.type === type)
    const template = plugin?.templates?.[0]
    if (!plugin || !template) {
      return
    }
    const newEntity = { ...template.template, id: shortUuid(), name }
    await guarded(type, async () => {
      // Resolves once the server has stored the entity, so the navigation below finds its collab room (#1895)
      const entity = await createEntity({ parent: null, entity: newEntity })
      if (!entity) {
        return
      }
      /** `layouts` is an array of specs: `Object.keys` on it gave '0', '1'…, so nothing ever opened in Edit */
      const layout = plugin.layouts?.some(spec => spec.name === 'Edit') ? 'Edit' : undefined
      /** A document you just created opens ready to edit, phones included (GH #2486; read-mode spec #2419, rule 1) */
      dispatch(features.ui.actions.setViewEditMode({ entityId: entity.id, edit: true }))
      navigateToEntity(entity, { layout })
      if (template.onCreate) {
        // Fire-and-forget: the entity view is already mounting; onCreate side effects land while the user reads.
        void template.onCreate({ entity, parent: undefined, globalContext })
      }
    })
  }

  const createSpace = async (name = 'New Space') =>
    guarded('space', async () => {
      const space = await graph
        .createEntity({ parent: null, entity: { type: 'folder', name, tags: ['space'] } })
        .unwrap()
      if (space) {
        navigateToEntity(space)
      }
    })

  return { createFromTemplate, createSpace, busy }
}
